import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useRef } from 'react'
import { Switch, Route, useHistory, useLocation, useRouteMatch } from 'react-router-dom'
import { LiveReceptionistIndex, LiveReceptionistCreate, LiveReceptionistShow } from 'live-receptionist-react-components'

const AI_CONNECT_LEGACY = {
    id: 4,
    name: 'AI-Connect (Legacy)'
}
const LIVE_ANSWER_CONNECT = {
    id: 8,
    name: 'Live Answer-Connect'
}
const AI_RECEPTIONIST = {
    id: 10,
    name: 'AI-Receptionist'
}
const services = [AI_CONNECT_LEGACY, LIVE_ANSWER_CONNECT, AI_RECEPTIONIST]

/**
 * Renders the list, editor, and creation routes for a receptionist service.
 * @param {object} props - Component props.
 * @param {string} [props.serviceType] - Receptionist service to display.
 * @param {Function} [props.onScriptsLoaded] - Receives the loaded scripts.
 * @param {Function} [props.onScriptsLoadError] - Handles script loading failures.
 * @returns {React.ReactElement|null} The selected receptionist screen.
 */
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types -- JavaScript uses JSDoc and PropTypes.
function LiveReceptionistCommunicator ({ serviceType, onScriptsLoaded, onScriptsLoadError }) {
    const history = useHistory()
    const { pathname } = useLocation()
    const match = useRouteMatch()
    const saveRedirectTimeout = useRef(null)

    const clearSaveRedirectTimeout = useCallback(() => {
        if (saveRedirectTimeout.current !== null) {
            clearTimeout(saveRedirectTimeout.current)
            saveRedirectTimeout.current = null
        }
    }, [])

    useEffect(() => clearSaveRedirectTimeout, [clearSaveRedirectTimeout])

    const service = services.find(s => s.name === serviceType)
    if (!service) return null

    const basePath = match.url.endsWith('/') ? match.url.slice(0, -1) : match.url
    let routeId = null
    if (pathname.includes('show') || pathname.includes('create')) {
        routeId = parseInt(pathname.substring(pathname.lastIndexOf('/') + 1))
    }
    const onClickLink = (route) => {
        if (route.name === 'live-answer.show' && route.params.id) {
            history.push(`${basePath}/show/${route.params.id}`)
        }
    }
    const onClickVendor = (id) => {
        history.push(`${basePath}/create/${id}`)
    }
    const onSave = () => {
        clearSaveRedirectTimeout()
        saveRedirectTimeout.current = setTimeout(() => {
            saveRedirectTimeout.current = null
            history.replace(basePath)
        }, 3000)
    }
    const onCancel = () => {
        clearSaveRedirectTimeout()
        history.replace(basePath)
    }

    return (
        <div data-testid='live-receptionist'>
            <Switch>
                <Route path={basePath} exact render={() => (
                    <LiveReceptionistIndex
                        onClickLink={onClickLink}
                        onClickVendor={onClickVendor}
                        data-testid='live-receptionist-list'
                        title={service.name}
                        vendor_id={service.id}
                        onScriptsLoaded={onScriptsLoaded}
                        onScriptsLoadError={onScriptsLoadError}
                    />
                )}/>
                <Route path={`${basePath}/show/${routeId}`} render={() => (
                    <LiveReceptionistShow
                        id={routeId}
                        postSaveCb={onSave}
                        onClickCancel={onCancel}
                        data-testid='live-receptionist-show'
                    />
                )}/>
                <Route path={`${basePath}/create/${routeId}`} render={() => (
                    <LiveReceptionistCreate
                        vendor_id={routeId}
                        postSaveCb={onSave}
                        onClickCancel={onCancel}
                        data-testid='live-receptionist-create'
                    />
                )}/>
            </Switch>
        </div>
    )
}

LiveReceptionistCommunicator.propTypes = {
    serviceType: PropTypes.string,
    onScriptsLoaded: PropTypes.func,
    onScriptsLoadError: PropTypes.func
}
export default LiveReceptionistCommunicator
